import { useState } from "react";
import { Mail, MapPin, ChevronDown, Copy } from "lucide-react";
import HeaderDash from "../../components/HeaderDash";
import { useAppConfigContext } from "../../context/AppConfigContext";

function UserContact() {
  const { config } = useAppConfigContext();
  const countryCodes = [
    { code: "+234", label: "Nigeria", flag: "🇳🇬" },
    { code: "+1", label: "United States", flag: "🇺🇸" },
    { code: "+44", label: "United Kingdom", flag: "🇬🇧" },
    { code: "+91", label: "India", flag: "🇮🇳" },
    { code: "+61", label: "Australia", flag: "🇦🇺" },
  ];

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    message: "",
    agree: false,
  });

  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [countryQuery, setCountryQuery] = useState("");
  const filteredCountryCodes = countryQuery.trim()
    ? countryCodes.filter(
        (country) =>
          country.code.toLowerCase().includes(countryQuery.trim().toLowerCase()) ||
          country.label.toLowerCase().includes(countryQuery.trim().toLowerCase())
      )
    : countryCodes;

  const supportEmail = config.supportEmail;
  const [copiedEmail, setCopiedEmail] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form Submitted:", { ...formData, countryCode: selectedCountry.code });
    alert("Message sent successfully!");
  };

  const handleCopyEmail = () => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(supportEmail);
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <HeaderDash />

      <main className="space-y-16 py-10">
        <section className="mx-auto max-w-[80%] px-4 text-center">
          <div className="space-y-4">
            <h1 className="text-2xl font-semibold text-[#61013B]">Contact us</h1>
            <p className="mx-auto max-w-3xl text-gray-700 font-medium">
              Questions, feedback, or partnership ideas? We'd love to hear from you.
              Whether you're a traveler, service provider, or just exploring what
              PULO has to offer - we're here to help.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-4 font-medium sm:grid-cols-2">
            <div className="flex flex-col items-center gap-2">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#f7ecf4]">
                <MapPin className="h-5 w-5 text-[#570033]" />
              </div>
              <p className="text-sm text-gray-800">Lagos, Nigeria</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#f7ecf4]">
                <Mail className="h-5 w-5 text-[#570033]" />
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCopyEmail}
                  className="inline-flex items-center gap-1 text-sm text-gray-800 hover:text-[#570033]"
                >
                  {supportEmail}
                  <Copy className="h-4 w-4" />
                </button>
                {copiedEmail && (
                  <span className="text-xs font-semibold text-[#570033]">
                    Copied!
                  </span>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-[80%] items-center gap-6 px-4 md:grid-cols-2">
          <div className="overflow-hidden rounded-xl border border-gray-100 shadow-sm">
            <img
              src="/images/support.jpg"
              alt="Support"
              className="h-full w-full object-cover"
            />
          </div>

          <form onSubmit={handleSubmit} className="ml-auto w-full space-y-6 sm:max-w-sm">
            <div className="grid grid-cols-1 gap-6">
              <div className="text-left">
                <label className="mb-1 block text-xs text-gray-800">
                  Enter Full Name
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your full name"
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm font-medium opacity-80 outline-none placeholder:font-medium placeholder:text-black"
                />
              </div>

              <div className="text-left">
                <label className="mb-1 block text-xs text-gray-800">
                  Enter Email
                </label>
                <div className="flex items-center gap-2 rounded border border-gray-300 px-3 py-2">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="you@example.com"
                    className="w-full text-sm font-medium opacity-80 outline-none placeholder:font-medium placeholder:text-black"
                  />
                </div>
              </div>

              <div className="text-left">
                <label className="mb-1 block text-xs text-gray-800">
                  Phone No
                </label>
                <div className="relative">
                  <div className="flex items-center gap-2 rounded-xl border border-gray-300 px-3 py-2">
                    <button
                      type="button"
                      className="flex items-center gap-2 border-r border-gray-200 pr-3"
                      onClick={() => setIsDropdownOpen((prev) => !prev)}
                    >
                      <span className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full text-lg">
                        {selectedCountry.flag}
                      </span>
                      <span className="text-sm font-semibold text-gray-800">
                        {selectedCountry.code}
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-500 transition-transform ${
                          isDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      required
                      placeholder="905 965 5425"
                      className="w-full text-sm outline-none placeholder:text-black"
                    />
                  </div>

                  {isDropdownOpen && (
                    <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
                      <div className="border-b border-gray-200 px-3 py-2">
                        <input
                          type="text"
                          value={countryQuery}
                          onChange={(e) => setCountryQuery(e.target.value)}
                          placeholder="Search country"
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-[#570033] focus:outline-none focus:ring-2 focus:ring-[#570033]/20"
                        />
                      </div>
                      <div className="max-h-60 overflow-auto">
                        {filteredCountryCodes.length ? (
                          filteredCountryCodes.map((country) => (
                            <button
                              key={country.code}
                              type="button"
                              onClick={() => {
                                setSelectedCountry(country);
                                setIsDropdownOpen(false);
                                setCountryQuery("");
                              }}
                              className="flex w-full items-center gap-3 px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
                            >
                              <span className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full text-lg">
                                {country.flag}
                              </span>
                              <span className="font-semibold">{country.code}</span>
                              <span className="text-xs text-gray-500">
                                {country.label}
                              </span>
                            </button>
                          ))
                        ) : (
                          <p className="px-3 py-3 text-sm text-gray-500">No results found</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="text-left">
                <label className="mb-1 block text-xs text-gray-800">
                  Additional Message
                </label>
                <textarea
                  rows="4"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  placeholder="Placeholder"
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm font-bold opacity-80 outline-none placeholder:font-medium placeholder:text-black"
                />
              </div>
            </div>

            <div className="mb-4 flex items-center gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                name="agree"
                checked={formData.agree}
                onChange={handleInputChange}
                required
                className="h-4 w-10 cursor-pointer appearance-none rounded border-[1px] border-[#61013B] checked:border-[#61013B] checked:bg-[#61013B]"
              />
              <p>
                By submitting this form, I agree to be contacted by PULO via email
                or phone regarding my inquiry. I also acknowledge that the
                information provided is subject to PULO's Privacy Policy.
              </p>
            </div>

            <button
              type="submit"
              className="w-full rounded-lg bg-[#570033] py-3 text-md font-semibold text-white"
            >
              Submit
            </button>
          </form>
        </section>
      </main>

    </div>
  );
}

export default UserContact;
